import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getPlans } from '../redux/planSlice';
import Pagination from '../components/Pagination';
import PlanDetailModal from '../components/PlanDetailModal';
import { useSearchExport } from '../context/SearchExportContext';
import { EyeIcon } from '@heroicons/react/16/solid';
import '../styles/spinner/spinner.css';
import ExportDropdown from '../context/ExportDropdown';
import RecordsLoader from '../components/RecordsLoader';

const PlansList = () => {
    const dispatch = useDispatch();
    const { plans: plansData, status } = useSelector((state) => state.plans); // Use plansData to differentiate
    const { filterData } = useSearchExport();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const plansPerPage = 50;

    useEffect(() => {
        dispatch(getPlans());  // Fetch all plans on component load
    }, [dispatch]);

    // Combine all plans into a single array for rendering
    const allPlans = [
        plansData?.freelistingPlan,
        plansData?.prolistingPlan,
        plansData?.ultimatelistingPlan,
    ].filter(plan => plan);  // Remove null or undefined plans

    // Filter the plans based on search term and current page
    const filteredPlans = filterData(allPlans)
        .slice((currentPage - 1) * plansPerPage, currentPage * plansPerPage);

    const handleRowClick = (plan) => {
        setSelectedPlan(plan);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedPlan(null);
    };

    return (
        <div className="container mx-auto p-6">

            {/* Render the loader if the status is 'idle' */}
            {status === 'idle' && <RecordsLoader text="Loading All Plans, please hold on..." />}

            <h1 className="text-2xl text-black font-bold mb-6">Plans List</h1>

            {/* Export Buttons */}
            <ExportDropdown data={allPlans} fileName="payments" />

            {status === 'failed' && <p>Error loading plans</p>}

            {/* Plans List Table */}
            {status === 'succeeded' && (
                <>
                    <table className="min-w-full table-auto bg-white shadow-md rounded-lg">
                        <thead>
                            <tr className="bg-gray-100 text-left">
                                <th className="px-4 py-2">Plan ID</th>
                                <th className="px-4 py-2">Title</th>
                                <th className="px-4 py-2">MRP</th>
                                <th className="px-4 py-2">Price</th>
                                <th className="px-4 py-2">Description</th>
                                <th className="px-4 py-2">Suggested</th>
                                <th className="px-4 py-2">View</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredPlans.length > 0 ? (
                                filteredPlans.map((plan) => (
                                    <tr
                                        key={plan.planId}
                                        className="border-b cursor-pointer hover:bg-gray-100"
                                    >
                                        <td className="px-4 py-2">{plan.planId}</td>
                                        <td className="px-4 py-2">{plan.title}</td>
                                        <td className="px-4 py-2">{plan.mrp}</td>
                                        <td className="px-4 py-2">{plan.price}</td>
                                        <td className="px-4 py-2">{plan.description}</td>
                                        <td className="px-4 py-2">{plan.isSuggested ? "Yes" : "No"}</td>
                                        <td className="px-4 py-2 flex">
                                            <EyeIcon className="size-6 text-blue-500 ml-4" onClick={() => handleRowClick(plan)} />
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="7" className="text-center py-4">No plans found!</td>
                                </tr>
                            )}
                        </tbody>
                    </table>

                    {/* Plan Detail Modal */}
                    <PlanDetailModal
                        isOpen={isModalOpen}
                        onClose={closeModal}
                        plan={selectedPlan}
                    />

                    {/* Pagination */}
                    {allPlans.length > plansPerPage && (
                        <Pagination
                            usersPerPage={plansPerPage}
                            totalUsers={allPlans.length}
                            paginate={setCurrentPage}
                            currentPage={currentPage}
                        />
                    )}
                </>
            )}
        </div>
    );
};

export default PlansList;